import React, { useContext } from "react"
import {
	Flex,
	Accordion,
	useAccordionActiveAccordion,
	Text,
	scale,
} from "@capioweb/ui-components"
import {
	LinkProperty,
	Property,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout, TwoColumnGridLayout } from "../../utils/layout"
import {
	ContactAccordionItem,
	ContactAccordionBaseBlockItem,
} from "./ContactAccordionItem"
import { useHandleHashChange, useSetCols } from "../../hooks"
import styled from "styled-components"
import { AnchorLinkContext } from "../../context"

type AccordionBaseContentAreaProperty = Property<
	ContactAccordionBaseBlockItem[]
>
interface CapioNexusContactSectionBlockProps {
	heading: StringProperty
	ingress: StringProperty
	contactBlocks: AccordionBaseContentAreaProperty
	anchorId?: StringProperty
	facebookHeading?: StringProperty
	facebookLinkItem?: LinkProperty
}

const UnitFacebookLink = styled.div`
	text-align: center;
	margin-top: ${scale.px(5)};

	a:hover {
		color: ${({ theme }) => theme.colors.hoverBlue};
	}
`

const CapioNexusContactSectionBlock: React.FC<CapioNexusContactSectionBlockProps> =
	({ contactBlocks, ...rest }) => {
		const anchorLinkContext = useContext(AnchorLinkContext)

		const facebookLink = rest.facebookLinkItem

		// Listen for hash on popState and update active accordion ID
		const { activeId, setActiveId } = useAccordionActiveAccordion(null)
		useHandleHashChange(setActiveId)

		// Split layout columns in two for screens > breakpoints.l
		const { cols } = useSetCols(contactBlocks?.value ?? [])

		React.useEffect(() => {
			setActiveId(anchorLinkContext.activeId)
		}, [])

		return (
			<BlockCommonLayout
				id={rest.anchorId?.value || null}
				description={rest.ingress}
				{...rest}
			>
				{contactBlocks?.value && cols.length > 0 && (
					<Flex w={1} flexDirection="column" alignItems="center">
						<TwoColumnGridLayout
							items={cols.map((col, i) => (
								<Accordion key={`acc-${i}`} activeId={activeId} controlled>
									{col.map((item, colI) => (
										<ContactAccordionItem
											{...item}
											setActiveId={setActiveId}
											activeId={activeId}
											key={colI}
										/>
									))}
								</Accordion>
							))}
						/>
					</Flex>
				)}
				{facebookLink && (
					<UnitFacebookLink>
						{rest.facebookHeading.value && (
							<Text variant="headline-3" fontSize={2.25} mb={1}>
								{rest.facebookHeading.value}
							</Text>
						)}
						<Text.Anchor
							href={facebookLink.href}
							target={facebookLink.target}
							title={facebookLink.title}
							textDecoration="underline"
						>
							{facebookLink.text}
						</Text.Anchor>
					</UnitFacebookLink>
				)}
			</BlockCommonLayout>
		)
	}

export { CapioNexusContactSectionBlock }
